// Draagt de HTTP-statuscode mee zodat callers een echte 401 (niet-ingelogd)
// kunnen onderscheiden van een transiënte serverfout (bv. 500 door tijdelijke
// DB-contentie) — alleen een 401 betekent dat de sessie ongeldig is.
export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

export function getAuthHeader(): string {
  const token = localStorage.getItem('token')
  return token ? `Bearer ${token}` : ''
}

export function handleUnauthorized(res: Response): void {
  if (res.status === 401) {
    localStorage.removeItem('token')
    window.location.reload()
  }
}

// FastAPI's OWN automatic request-body validation (Pydantic `ValidationError`,
// e.g. a `@field_validator` failure) returns `detail` as an ARRAY of error
// objects (`{type, loc, msg, ...}`), not a string — unlike this app's own
// domain-error handlers, which always pass a plain string via
// `HTTPException(..., detail=str(exc))`. Passing that array straight into
// `Error`'s constructor lets JS's default `String(value)` coercion silently
// produce the literal text "[object Object]" (a single-item array's
// `toString()`/`join()` calls each element's own `toString()`, which for a
// plain object is `[object Object]`, with no separator to reveal there were
// multiple items). This surfaced in production as `svg[object Object]` next
// to a warning-icon `<svg>` in an error banner. Every non-string shape is
// normalized here so `ApiError.message` is always human-readable.
function formatApiErrorDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail
  const messageOf = (item: unknown): string | null => {
    if (item && typeof item === 'object' && 'msg' in item && typeof (item as { msg: unknown }).msg === 'string') {
      return (item as { msg: string }).msg
    }
    return null
  }
  if (Array.isArray(detail)) {
    const messages = detail.map(item => messageOf(item) ?? JSON.stringify(item)).filter(Boolean)
    return messages.length > 0 ? messages.join('; ') : fallback
  }
  if (detail && typeof detail === 'object') {
    return messageOf(detail) ?? JSON.stringify(detail)
  }
  return fallback
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader(),
      ...options?.headers,
    },
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new ApiError(res.status, formatApiErrorDetail(body.detail, res.statusText))
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

export async function downloadBlob(res: Response, fallbackFilename: string): Promise<void> {
  const blob = await res.blob()
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename = match ? match[1] : fallbackFilename
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
